import { Link } from 'react-router-dom';
import { ArrowRight, Star, Truck, Shield, RotateCcw, CreditCard } from 'lucide-react';
import { HeroBanner } from '../components/HeroBanner';
import { ProductGrid } from '../components/ProductGrid';
import { CategorySection } from '../components/CategorySection';
import { OfferBanner } from '../components/OfferBanner';
import { Newsletter } from '../components/Newsletter';
import { getFeaturedProducts, getNewProducts, getSaleProducts } from '../data/products';
import { useState, useEffect } from 'react';

export const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState<any[]>([]);
  const [newProducts, setNewProducts] = useState<any[]>([]);
  const [saleProducts, setSaleProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const [featured, newProds, sale] = await Promise.all([
          getFeaturedProducts(8),
          getNewProducts(8),
          getSaleProducts(8),
        ]);
        setFeaturedProducts(featured);
        setNewProducts(newProds);
        setSaleProducts(sale);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading products:', error);
        setIsLoading(false);
      }
    };
    loadProducts();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroBanner />

      {/* Features Bar */}
      <div className="bg-white border-b">
        <div className="container py-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-center justify-center space-x-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <Truck className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900">Free Shipping</h4>
                <p className="text-sm text-charcoal-600">On orders above ₹999</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <RotateCcw className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900">Easy Returns</h4>
                <p className="text-sm text-charcoal-600">30-day return policy</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <Shield className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900">Secure Payment</h4>
                <p className="text-sm text-charcoal-600">100% secure checkout</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900">Multiple Payment</h4>
                <p className="text-sm text-charcoal-600">All major cards accepted</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Section */}
      <CategorySection />

      {/* Featured Products */}
      <section className="section-padding premium-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-charcoal-900 mb-6">
              Featured <span className="text-gradient">Products</span>
            </h2>
            <p className="text-xl text-charcoal-600 max-w-3xl mx-auto leading-relaxed">
              Discover our handpicked collection of premium products that combine style, quality, and comfort.
            </p>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <ProductGrid products={featuredProducts} />
          )}
          <div className="text-center mt-12">
            <Link
              to="/shop?featured=true"
              className="btn-primary btn-lg inline-flex items-center space-x-3"
            >
              <span>View All Featured</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Offer Banner */}
      <OfferBanner />

      {/* New Arrivals */}
      <section className="section-padding bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              New Arrivals
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Be the first to discover our latest collection of trendy and fashionable products.
            </p>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <ProductGrid products={newProducts} />
          )}
          <div className="text-center mt-8">
            <Link
              to="/shop?new=true"
              className="btn-primary btn-lg inline-flex items-center space-x-2"
            >
              <span>View All New Arrivals</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Sale Products */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              On Sale
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Don't miss out on these amazing deals. Limited time offers on selected items.
            </p>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <ProductGrid products={saleProducts} />
          )}
          <div className="text-center mt-8">
            <Link
              to="/shop?sale=true"
              className="btn-primary btn-lg inline-flex items-center space-x-2"
            >
              <span>View All Sale Items</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Read reviews from our satisfied customers who love our products and service.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Priya Sharma",
                location: "Mumbai",
                rating: 5,
                comment: "Amazing quality and fast delivery! The products exceeded my expectations. Will definitely order again."
              },
              {
                name: "Rajesh Kumar",
                location: "Delhi",
                rating: 5,
                comment: "Great customer service and the products are exactly as described. Highly recommended!"
              },
              {
                name: "Anita Patel",
                location: "Bangalore",
                rating: 5,
                comment: "Love the variety and quality of products. The shopping experience was smooth and enjoyable."
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">"{testimonial.comment}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
};
